/**
 * TrustedDeviceBanner — admin-only chip that reassures platform
 * super-admins / editors that this device won't auto-sign-them-out.
 * Dismissible; persists dismissal per device via localStorage.
 *
 * Renders once at the app root; silent for guests and regular users.
 */
import { useEffect, useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LS_KEY = "sp_trusted_device_banner_dismissed_v1";

export default function TrustedDeviceBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid flash

  const isPlatformAdmin = !!(
    user && (
      user.is_admin ||
      user.is_super_admin ||
      ["admin", "super_admin", "editor", "organization_admin"].includes(user.platform_role)
    )
  );

  useEffect(() => {
    if (!isPlatformAdmin) { setDismissed(true); return; }
    try {
      setDismissed(localStorage.getItem(LS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, [isPlatformAdmin, user?.id]);

  if (!isPlatformAdmin || dismissed) return null;

  const close = () => {
    try { localStorage.setItem(LS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const displayName = user?.name || user?.full_name || (user?.email || "").split("@")[0] || "admin";

  return (
    <div
      className="fixed z-[60] left-1/2 -translate-x-1/2 top-[calc(env(safe-area-inset-top,0px)+72px)] sm:top-[calc(env(safe-area-inset-top,0px)+80px)]"
      role="status"
      aria-live="polite"
      data-testid="trusted-device-banner"
    >
      <div
        className="flex items-center gap-2 rounded-full pl-3 pr-1 py-1 shadow-lg"
        style={{
          background: "linear-gradient(120deg, #FFF6E8 0%, #FFEACC 100%)",
          border: "1px solid rgba(178, 93, 56, 0.35)",
          color: "#3D2A17",
        }}
      >
        <ShieldCheck className="w-4 h-4" style={{ color: "#B25D38" }} aria-hidden />
        <span className="text-[12px] leading-tight">
          <strong className="font-semibold">You're signed in, {displayName}.</strong>{" "}
          <span className="opacity-80">This trusted device won't auto-sign you out — bookmark away.</span>
        </span>
        <button
          type="button"
          onClick={close}
          className="ml-1 p-1 rounded-full hover:bg-white/60"
          data-testid="trusted-device-banner-dismiss"
          aria-label="Dismiss"
          title="Dismiss"
        >
          <X className="w-3.5 h-3.5 opacity-70" />
        </button>
      </div>
    </div>
  );
}
